import { useMemo, useState } from 'react';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import { baseFieldClasses } from '../components/fieldClasses';
import { ConfirmModal } from '../files/ConfirmModal';
import { docFromPlainText, tiptapDocToPlainText } from '../rich-editor/plainText';
import { RichEditor } from '../rich-editor/RichEditor';
import type { TiptapDoc } from '../rich-editor/types';

const MAX_TITLE_LENGTH = 200;
// Must match the server's cap on the trimmed plain-text projection of the doc.
const MAX_BODY_LENGTH = 20000;
const BODY_WARN_RATIO = 0.9;
const EDITOR_MIN_HEIGHT_PX = 240;
const TITLE_INPUT_ID = 'protocol-editor-title';

interface ProtocolEditorProtocol {
    title: string;
    body: string;
    bodyDoc: TiptapDoc | null;
}

export interface ProtocolEditorFields {
    title: string;
    bodyDoc: TiptapDoc;
}

interface ProtocolEditorProps {
    protocol: ProtocolEditorProtocol | null;
    isSaving: boolean;
    isDeleting?: boolean;
    onClose: () => void;
    onSave: (fields: ProtocolEditorFields) => void;
    onDelete?: () => void;
}

function formatCount(n: number): string {
    return n.toLocaleString('en-US');
}

function BodyCharCount({ length }: { length: number }) {
    let tone = 'text-gray-400';
    if (length > MAX_BODY_LENGTH) tone = 'text-red-500';
    else if (length >= MAX_BODY_LENGTH * BODY_WARN_RATIO) tone = 'text-amber-600';
    return (
        <span className={`text-[11px] font-medium tabular-nums ${tone}`}>
            {formatCount(length)} / {formatCount(MAX_BODY_LENGTH)}
        </span>
    );
}

interface BodyFieldProps {
    value: TiptapDoc;
    onChange: (doc: TiptapDoc) => void;
    length: number;
}

function BodyField({ value, onChange, length }: BodyFieldProps) {
    return (
        <div className="flex flex-col gap-1">
            <RichEditor
                value={value}
                onChange={onChange}
                allowImages={false}
                autoFocus={false}
                minHeightPx={EDITOR_MIN_HEIGHT_PX}
                toolbarRightSlot={<BodyCharCount length={length} />}
            />
            {length > MAX_BODY_LENGTH && (
                <p className="text-xs text-red-500">
                    Protocol is too long (max {formatCount(MAX_BODY_LENGTH)} characters).
                </p>
            )}
        </div>
    );
}

function TitleField({
    value,
    onChange,
    disabled
}: {
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
}) {
    return (
        <div className="flex flex-col gap-1">
            <label htmlFor={TITLE_INPUT_ID} className="text-xs font-semibold text-gray-700">
                Title
            </label>
            <input
                id={TITLE_INPUT_ID}
                type="text"
                value={value}
                maxLength={MAX_TITLE_LENGTH}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className={`${baseFieldClasses} disabled:opacity-60`}
            />
        </div>
    );
}

interface EditorActionsProps {
    isBusy: boolean;
    isSaving: boolean;
    canSave: boolean;
    onCancel: () => void;
    onSave: () => void;
    onDelete?: () => void;
}

function EditorActions({
    isBusy,
    isSaving,
    canSave,
    onCancel,
    onSave,
    onDelete
}: EditorActionsProps) {
    return (
        <div className="flex justify-between items-center gap-3">
            {onDelete ? (
                <button
                    type="button"
                    onClick={onDelete}
                    disabled={isBusy}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                </button>
            ) : (
                <span />
            )}
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isBusy}
                    className="px-4 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={onSave}
                    disabled={!canSave}
                    className="px-4 py-2 text-sm font-semibold text-white bg-gray-800 rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSaving ? 'Saving…' : 'Save'}
                </button>
            </div>
        </div>
    );
}

export function ProtocolEditor({
    protocol,
    isSaving,
    isDeleting = false,
    onClose,
    onSave,
    onDelete
}: ProtocolEditorProps) {
    const initialTitle = protocol?.title ?? '';
    const [initialDoc] = useState<TiptapDoc>(
        () => protocol?.bodyDoc ?? docFromPlainText(protocol?.body ?? '')
    );
    const [title, setTitle] = useState(initialTitle);
    const [doc, setDoc] = useState(initialDoc);

    const bodyLength = useMemo(() => tiptapDocToPlainText(doc).trim().length, [doc]);
    const isBusy = isSaving || isDeleting;
    const isDirty = title !== initialTitle || doc !== initialDoc;
    const canSave =
        title.trim().length > 0 && bodyLength > 0 && bodyLength <= MAX_BODY_LENGTH && !isBusy;

    const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);

    const discard = () => {
        if (isDirty) setIsConfirmingDiscard(true);
        else onClose();
    };

    const submit = () => {
        if (canSave) onSave({ title: title.trim(), bodyDoc: doc });
    };

    return (
        <div className="flex flex-col gap-4">
            <button
                type="button"
                onClick={discard}
                disabled={isBusy}
                className="flex items-center gap-1.5 w-fit text-sm text-gray-500 hover:text-gray-800 transition-colors disabled:opacity-50"
            >
                <ArrowLeftIcon className="w-3.5 h-3.5" />
                Back to protocols
            </button>

            <TitleField value={title} onChange={setTitle} disabled={isBusy} />

            <BodyField value={doc} onChange={setDoc} length={bodyLength} />

            <EditorActions
                isBusy={isBusy}
                isSaving={isSaving}
                canSave={canSave}
                onCancel={discard}
                onSave={submit}
                onDelete={onDelete}
            />

            {isConfirmingDiscard && (
                <ConfirmModal
                    title="Discard your unsaved changes?"
                    message="Your edits to this protocol will be lost."
                    confirmLabel="Discard"
                    onConfirm={onClose}
                    onCancel={() => setIsConfirmingDiscard(false)}
                />
            )}
        </div>
    );
}
